<script setup lang="ts">
  import Underline from '@tiptap/extension-underline'
  import StarterKit from '@tiptap/starter-kit'
  import { type Extensions, type JSONContent, EditorContent, useEditor } from '@tiptap/vue-3'

  import {
    CharacterCountWithValidation,
    EDITOR_CONTENT_LENGTH_DEFAULTS,
    ExtendedLink,
    isDeepEqual,
    useArticleEditorToolbar,
    useEditorContentValidation,
  } from '@/shared/lib'

  export interface ArticleEditorMessageKeys {
    required: string
    tooLong: string
    tooShort: string
  }

  const props = withDefaults(
    defineProps<{
      dataTestid?: string
      extensions?: Extensions
      maxLength?: number
      messageKeys: ArticleEditorMessageKeys
      minLength?: number
      modelValue: Nullable<JSONContent | string>
      required?: boolean
      toolbarAriaLabelKey: string
    }>(),
    {
      extensions: () => [],
      required: false,
    },
  )

  const emit = defineEmits<{
    'update:modelValue': [value: JSONContent]
  }>()

  const { modelValue, maxLength, minLength, required, messageKeys } = toRefs(props)
  const { MIN: MIN_LENGTH, MAX: MAX_LENGTH } = EDITOR_CONTENT_LENGTH_DEFAULTS

  const editor = useEditor({
    content: '',
    extensions: [
      StarterKit,
      Underline,
      ExtendedLink,
      CharacterCountWithValidation.configure({
        minLength: minLength?.value ?? MIN_LENGTH,
        maxLength: maxLength?.value ?? MAX_LENGTH,
        required: required?.value ?? false,
      }),
      ...props.extensions,
    ],
    onUpdate: () => {
      emit('update:modelValue', editor.value!.getJSON())
      touchContentValidation()
    },
  })

  const {
    contentCounterLabel,
    contentErrorMessage,
    isContentValid,
    isCounterOverLimit,
    isInvalid,
    resetValidation,
    showErrorMessage,
    touchContentValidation,
    validate,
  } = useEditorContentValidation({
    editor,
    minLength,
    maxLength,
    required,
    messageKeys: messageKeys.value,
  })

  onMounted(() => {
    watch(
      modelValue,
      (value) => {
        if (value === null || isDeepEqual(editor.value?.getJSON(), value)) {
          return
        }
        editor.value?.commands.setContent(value, false)
      },
      { immediate: true },
    )
  })

  defineExpose({
    isContentValid,
    resetValidation,
    validate,
  })

  const { toolbarActions } = useArticleEditorToolbar(editor)
</script>

<template>
  <div
    class="flex w-full flex-col"
    :data-testid="dataTestid"
  >
    <p
      v-if="showErrorMessage"
      class="text-error m-0 mb-1 text-xs"
    >
      {{ contentErrorMessage }}
    </p>

    <div
      v-if="editor"
      :class="[
        'flex h-83.25 w-full flex-col overflow-hidden rounded-lg border bg-white',
        isInvalid ? 'border-error' : 'border-border',
      ]"
    >
      <div
        class="border-border bg-surface-bg flex flex-wrap items-center gap-1.5 border-b p-2"
        role="toolbar"
        :aria-label="$t(toolbarAriaLabelKey)"
      >
        <button
          v-for="action in toolbarActions"
          :key="action.icon"
          type="button"
          class="hover:bg-secondary-bg text-body flex size-6 cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 transition-colors"
          :class="{ 'bg-secondary-bg': action.isActive }"
          :aria-label="action.label"
          :aria-pressed="action.isActive"
          tabindex="0"
          @click="action.run()"
          @keydown.enter.space.prevent="action.run()"
        >
          <i
            :class="['fa-regular', action.icon, 'text-xs']"
            aria-hidden="true"
          />
        </button>
        <span
          class="ms-auto text-xs"
          :class="isCounterOverLimit ? 'text-error' : 'text-body'"
          aria-live="polite"
        >
          {{ contentCounterLabel }}
        </span>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <EditorContent
          class="h-full outline-none"
          :editor="editor"
        />
      </div>
    </div>
  </div>
</template>
